import { useEffect, useState } from "react";
import {
  ApiError,
  createSuppression,
  fetchSuppressionHistory,
  fetchSuppressions,
  importSuppressions,
  releaseSuppression,
} from "@/lib/api";
import type {
  Suppression,
  SuppressionEvent,
  SuppressionImportResult,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { toast } from "sonner";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { DataTable, TablePager } from "@/components/emc/DataTable";
import { EnumText } from "@/components/emc/EnumText";
import { Field } from "@/components/emc/Field";
import { PageHeader } from "@/components/emc/PageHeader";
import { StatGrid, StatTile } from "@/components/emc/StatTile";
import { StatusPill } from "@/components/emc/StatusPill";
import { ToolbarForm } from "@/components/emc/Toolbar";
import { EmptyState, ErrorState } from "@/components/emc/states";
import { minute } from "@/components/emc/format";
import { labelOf, labelOptions } from "@/components/emc/status";

// The reasons a person may put on an exclusion. The rest of the enum —
// complaint, hard bounce, blocked — are signals the system learns, and the
// API refuses them from a form for the same reason: a log has to say what
// happened, not what somebody typed.
const MANUAL_REASONS = [
  { value: "manual", label: "Kézi döntés" },
  { value: "role_address", label: "Szerepcím (info@, sales@)" },
  { value: "disposable", label: "Eldobható cím" },
  { value: "spam_trap", label: "Spam-csapda" },
] as const;

// The filters offer every value the one label map knows, in Hungarian; the
// wire value is what goes to the API.
const ALL_REASONS = labelOptions("suppressionReason");
const SOURCES = labelOptions("suppressionSource");

const COUNTRIES = ["RO", "PL", "HU", "GR", "SK", "EU", "BG", "IT", "CZ"];
const PAGE_SIZE = 50;

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Ismeretlen hiba.";
}

function when(value: string | null): string {
  return value === null ? "—" : new Date(value).toLocaleString("hu-HU");
}

function scopeOf(row: Suppression): string {
  return row.country === "" ? "globális" : row.country;
}

export default function Suppressions() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [q, setQ] = useState("");
  const [reason, setReason] = useState("");
  const [source, setSource] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<Suppression[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [openEmail, setOpenEmail] = useState<string | null>(null);
  const [history, setHistory] = useState<SuppressionEvent[]>([]);

  const [newEmail, setNewEmail] = useState("");
  const [newReason, setNewReason] = useState<string>("manual");
  const [newCountry, setNewCountry] = useState("");
  const [newNote, setNewNote] = useState("");

  const [file, setFile] = useState<File | null>(null);
  const [fileReason, setFileReason] = useState<string>("manual");
  const [imported, setImported] = useState<SuppressionImportResult | null>(null);

  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  function reload(nextPage = page) {
    fetchSuppressions({ q, reason, source, page: nextPage, page_size: PAGE_SIZE })
      .then((body) => {
        setRows(body.items);
        setHasMore(body.has_more);
        setListError(null);
      })
      .catch((error: unknown) => setListError(errorMessage(error)));
  }

  useEffect(() => {
    reload(1);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reason, source]);

  function onSearch(event: React.FormEvent) {
    event.preventDefault();
    setPage(1);
    reload(1);
  }

  function openHistory(email: string) {
    if (openEmail === email) {
      setOpenEmail(null);
      setHistory([]);
      return;
    }
    setOpenEmail(email);
    fetchSuppressionHistory(email)
      .then(setHistory)
      .catch((error: unknown) => setListError(errorMessage(error)));
  }

  async function onCreate(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      await createSuppression({
        email: newEmail,
        reason: newReason,
        country: newCountry,
        note: newNote,
      });
      setNewEmail("");
      setNewNote("");
      reload();
    } catch (error: unknown) {
      setFormError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function onUpload(event: React.FormEvent) {
    event.preventDefault();
    if (file === null) return;
    setBusy(true);
    setFormError(null);
    try {
      setImported(await importSuppressions(file, fileReason));
      reload();
    } catch (error: unknown) {
      setFormError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  // The release is asked for in a dialog, not `window.prompt`: it says what
  // happens, holds the reason the log keeps, and shows a refusal in place.
  const [releasing, setReleasing] = useState<Suppression | null>(null);

  async function release(row: Suppression, note: string) {
    await releaseSuppression(row.email, { country: row.country, note });
    toast.success("A kizárás feloldva", { description: row.email });
    reload();
    if (openEmail === row.email) {
      setHistory(await fetchSuppressionHistory(row.email));
    }
  }

  // Eight columns that fit the 1144 px a 1440 px screen leaves for content:
  // the two signal times share a cell and are shown to the minute, and the
  // address, the reason and the source may wrap — so the release button at
  // the end is never scrolled out of sight.
  const columns: ColumnDef<Suppression>[] = [
    {
      id: "email",
      header: "Email",
      cell: ({ row }) => <span className="font-medium">{row.original.email}</span>,
      meta: { wrap: true, className: "wrap-anywhere" },
    },
    { id: "scope", header: "Hatókör", cell: ({ row }) => scopeOf(row.original), meta: { mono: true } },
    {
      id: "reason",
      header: "Ok",
      cell: ({ row }) => <EnumText domain="suppressionReason" value={row.original.reason} />,
      meta: { wrap: true },
    },
    {
      id: "source",
      header: "Forrás",
      cell: ({ row }) => <EnumText domain="suppressionSource" value={row.original.source} />,
      meta: { wrap: true, muted: true },
    },
    {
      id: "signals",
      header: "Jelek",
      cell: ({ row }) => row.original.signal_count,
      meta: { align: "right" },
    },
    {
      id: "last",
      header: "Utolsó jel",
      cell: ({ row }) => (
        <span className="flex flex-col items-start gap-0.5">
          <span className="data-mono">{minute(row.original.last_signal_at)}</span>
          {row.original.signal_count > 1 && (
            <span className="data-mono text-label text-muted-foreground">
              első: {minute(row.original.first_signal_at)}
            </span>
          )}
        </span>
      ),
    },
    {
      id: "released",
      header: "Állapot",
      cell: ({ row }) => {
        const found = row.original;
        if (found.released_at !== null) {
          return (
            <span className="flex flex-col items-start gap-0.5">
              <StatusPill domain="suppression" state="released" />
              <span className="data-mono text-label text-muted-foreground">
                {minute(found.released_at)}
              </span>
            </span>
          );
        }
        if (found.reason === "held") {
          // Rule 1, 2026-10-16: a hold, live until its end, then over by itself.
          const live = found.is_active !== false;
          return (
            <span className="flex flex-col items-start gap-0.5">
              <StatusPill domain="suppression" state={live ? "held" : "ended"} />
              {found.hold_cause ? (
                <span className="text-label text-muted-foreground">
                  {labelOf("holdCause", found.hold_cause)}
                </span>
              ) : null}
              <span className="data-mono text-label text-muted-foreground">
                {live ? "eddig: " : "lejárt: "}
                {found.expires_at ? minute(found.expires_at) : "—"}
              </span>
            </span>
          );
        }
        return <StatusPill domain="suppression" state="active" />;
      },
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Műveletek</span>,
      cell: ({ row }) =>
        isAdmin &&
        row.original.released_at === null &&
        row.original.is_active !== false && (
          <Button
            variant="outline"
            size="sm"
            type="button"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              setReleasing(row.original);
            }}
          >
            Feloldás
          </Button>
        ),
      meta: { align: "right" },
    },
  ];

  return (
    <>
      <ActionDialog
        open={releasing !== null}
        onOpenChange={(open) => !open && setReleasing(null)}
        title="A kizárás feloldása"
        description={
          <>
            A(z) <span className="data-mono">{releasing?.email}</span> újra kaphat levelet
            {releasing?.country ? ` (${releasing.country})` : " minden országban"}. A feloldás a
            naplóba kerül a neveddel és az indoklással; a jelek, amik miatt kizártuk, megmaradnak.
          </>
        }
        reason={{ label: "Miért oldjuk fel?", placeholder: "pl. a panasz tévedés volt, a címzett írásban jelezte", max: 300 }}
        confirmLabel="Feloldás"
        onConfirm={async (note) => {
          if (releasing !== null) await release(releasing, note);
        }}
      />
      <PageHeader
        title="Kizárások"
        description={
          <>
            Aki innen kikerül, egyetlen kampányt sem kap — semmilyen országban. A „tartás” a
            kivétel: nem a címről szóló jel miatt áll, és a végén magától lejár. Feloldani csak
            admin tud, indoklással, és a döntés bekerül a naplóba.
          </>
        }
      />

      <ToolbarForm className="mb-3" onSubmit={onSearch}>
        <Input
          aria-label="Cím eleje"
          className="w-72"
          placeholder="Cím eleje"
          value={q}
          onChange={(event) => setQ(event.target.value)}
        />
        <NativeSelect aria-label="Ok" value={reason} onChange={(event) => setReason(event.target.value)}>
          <option value="">Minden ok</option>
          {ALL_REASONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label="Forrás"
          value={source}
          onChange={(event) => setSource(event.target.value)}
        >
          <option value="">Minden forrás</option>
          {SOURCES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </NativeSelect>
        <Button type="submit">Keresés</Button>
      </ToolbarForm>

      {listError !== null && <ErrorState message={listError} className="mb-4" />}

      <DataTable
        label="Kizárások"
        columns={columns}
        data={rows}
        getRowId={(row) => String(row.id)}
        onRowClick={(row) => openHistory(row.email)}
        isRowSelected={(row) => row.email === openEmail}
        renderExpanded={(row) =>
          openEmail === row.email ? (
            <div className="flex flex-col gap-1 rounded-control border border-line bg-surface p-3">
              {history.length === 0 ? (
                <span className="text-muted-foreground">Nincs naplóbejegyzés.</span>
              ) : (
                history.map((event) => (
                  <p key={event.id} className="text-muted-foreground">
                    <span className="data-mono">{when(event.occurred_at)}</span> ·{" "}
                    <strong className="font-medium text-ink" title={event.action}>
                      {labelOf("suppressionAction", event.action)}
                    </strong>{" "}
                    · <EnumText domain="suppressionReason" value={event.reason} /> ·{" "}
                    <EnumText domain="suppressionSource" value={event.source} /> · {event.actor}
                    {typeof event.detail.note === "string" && event.detail.note !== ""
                      ? ` · „${event.detail.note}”`
                      : ""}
                  </p>
                ))
              )}
            </div>
          ) : null
        }
        empty={<EmptyState bare title="Nincs találat." />}
        footer={
          <TablePager summary={`${page}. oldal`}>
            <Button
              variant="outline"
              type="button"
              disabled={page === 1}
              onClick={() => {
                setPage(page - 1);
                reload(page - 1);
              }}
            >
              Előző
            </Button>
            <Button
              variant="outline"
              type="button"
              disabled={!hasMore}
              onClick={() => {
                setPage(page + 1);
                reload(page + 1);
              }}
            >
              Következő
            </Button>
          </TablePager>
        }
      />

      {formError !== null && <ErrorState message={formError} className="mt-6" />}

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Kézi kizárás</CardTitle>
            <CardDescription>
              Egy cím, egy ok és egy indoklás. Az ok itt csak emberi döntés lehet; a
              visszajelzésből tanult jeleket a rendszer írja.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="grid gap-4 md:grid-cols-2" onSubmit={onCreate}>
              <Field label="Cím" htmlFor="suppress-email" className="md:col-span-2">
                <Input
                  id="suppress-email"
                  placeholder="cím@example.hu"
                  value={newEmail}
                  onChange={(event) => setNewEmail(event.target.value)}
                  required
                />
              </Field>
              <Field label="Ok" htmlFor="suppress-reason">
                <NativeSelect
                  id="suppress-reason"
                  className="w-full"
                  value={newReason}
                  onChange={(event) => setNewReason(event.target.value)}
                >
                  {MANUAL_REASONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Hatókör" htmlFor="suppress-country">
                <NativeSelect
                  id="suppress-country"
                  className="w-full"
                  value={newCountry}
                  onChange={(event) => setNewCountry(event.target.value)}
                >
                  <option value="">Minden ország</option>
                  {COUNTRIES.map((code) => (
                    <option key={code} value={code}>
                      Csak {code}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Indoklás" htmlFor="suppress-note" className="md:col-span-2">
                <Input
                  id="suppress-note"
                  placeholder="Indoklás"
                  value={newNote}
                  onChange={(event) => setNewNote(event.target.value)}
                />
              </Field>
              <div>
                <Button variant="destructive" type="submit" disabled={busy}>
                  Kizárás
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Fájl feltöltése</CardTitle>
            <CardDescription>
              CSV vagy TXT, soronként egy cím — vagy egy <code>email</code> oszlop.
              Legfeljebb 20 MB. Ami feltöltésre kerül, az globális kizárás lesz.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <form className="grid gap-4 md:grid-cols-2" onSubmit={onUpload}>
              <Field label="Fájl" htmlFor="suppress-file">
                <Input
                  id="suppress-file"
                  type="file"
                  accept=".csv,.txt,text/csv,text/plain"
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                />
              </Field>
              <Field label="Ok" htmlFor="suppress-file-reason">
                <NativeSelect
                  id="suppress-file-reason"
                  className="w-full"
                  value={fileReason}
                  onChange={(event) => setFileReason(event.target.value)}
                >
                  {MANUAL_REASONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <div>
                <Button type="submit" disabled={busy || file === null}>
                  Feltöltés
                </Button>
              </div>
            </form>

            {imported !== null && (
              <>
                <StatGrid>
                  <StatTile size="sm" label="Új kizárás" value={imported.suppressed} />
                  <StatTile size="sm" label="Már ismert cím" value={imported.reinforced} />
                  <StatTile size="sm" label="Hibás sor" value={imported.invalid} />
                </StatGrid>
                {imported.sample_invalid.length > 0 && (
                  <div className="flex flex-col gap-1 rounded-control border border-line bg-surface-2 p-3">
                    <p className="caps-label mb-1">Hibás sorok (minta)</p>
                    {imported.sample_invalid.map((line, index) => (
                      <div key={index} className="font-mono break-all">
                        {line}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
